import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Info, Heart, Crown, Timer } from 'lucide-react';
import { toast } from 'sonner';

const FAV_STORAGE_KEY = 'intimate:hero:favourite-series';

function readFavourites() {
  try {
    const raw = localStorage.getItem(FAV_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeFavourites(list) {
  try {
    localStorage.setItem(FAV_STORAGE_KEY, JSON.stringify(list));
  } catch { /* storage disabled */ }
}

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!targetIso) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [targetIso]);

  if (!targetIso) return null;
  const target = Date.parse(targetIso);
  if (Number.isNaN(target)) return null;
  const diff = target - now;
  if (diff <= 0) return null; // expired → caller hides the countdown
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds };
}

function CountdownBlock({ label, target }) {
  const parts = useCountdown(target);
  if (!parts) return null;
  const pad = (n) => String(n).padStart(2, '0');
  const cells = [
    { value: parts.days, unit: parts.days === 1 ? 'dia' : 'dias' },
    { value: pad(parts.hours), unit: 'h' },
    { value: pad(parts.minutes), unit: 'min' },
    { value: pad(parts.seconds), unit: 'seg' },
  ];
  return (
    <div
      className="mb-5 inline-flex flex-wrap items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-xl bg-black/40 border border-white/15 backdrop-blur-sm w-fit"
      data-testid="editorial-hero-countdown"
    >
      <div className="flex items-center gap-1.5 text-gold text-xs sm:text-sm font-semibold uppercase tracking-wider pr-2 sm:pr-3 border-r border-white/15">
        <Timer className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="flex items-center gap-1.5 sm:gap-2">
        {cells.map((c, i) => (
          <div key={i} className="flex flex-col items-center min-w-[40px] sm:min-w-[48px]">
            <span className="font-brand font-bold text-white text-lg sm:text-2xl leading-none tabular-nums">
              {c.value}
            </span>
            <span className="text-[10px] sm:text-[11px] text-white/60 uppercase tracking-wider mt-0.5">
              {c.unit}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * EditorialHero — renders the admin-configured featured series at the very
 * top of the homepage. Reads live data from GET /api/homepage/hero. Silently
 * renders nothing when the hero is disabled (or the series was removed), so
 * the rest of the homepage keeps flowing untouched.
 */
export default function EditorialHero() {
  const navigate = useNavigate();
  const [hero, setHero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavourite, setIsFavourite] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/homepage/hero');
        if (!cancelled) {
          setHero(data);
          if (data?.enabled && data.series) {
            setIsFavourite(readFavourites().includes(data.series.id));
          }
        }
      } catch {
        if (!cancelled) setHero({ enabled: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section className="pt-20 md:pt-24 px-4 md:px-12" data-testid="editorial-hero-loading">
        <div className="max-w-6xl mx-auto rounded-3xl bg-surface/40 border border-border animate-pulse min-h-[280px] md:min-h-[420px]" />
      </section>
    );
  }

  if (!hero?.enabled || !hero.series) return null;

  const { series, label, custom_synopsis, background_image, is_unlocked, countdown_enabled, countdown_target_date, countdown_label } = hero;
  const synopsis = (custom_synopsis || series.description || '').trim();
  const image = background_image || series.cover_image || '';

  const handleStartReading = () => {
    if (is_unlocked && series.first_episode_story_id) {
      navigate(`/story/${series.first_episode_story_id}`);
      return;
    }
    if (is_unlocked && !series.first_episode_story_id) {
      // Access but no episodes yet — send to detail page.
      navigate(`/series/${series.slug || series.id}`);
      return;
    }
    // No access → existing Premium upgrade flow.
    navigate('/premium');
  };

  const handleMoreInfo = () => {
    navigate(`/series/${series.slug || series.id}`);
  };

  const handleToggleFavourite = (e) => {
    e.stopPropagation();
    const list = readFavourites();
    const idx = list.indexOf(series.id);
    if (idx >= 0) {
      list.splice(idx, 1);
      writeFavourites(list);
      setIsFavourite(false);
      toast.success('Removido dos favoritos');
    } else {
      list.unshift(series.id);
      writeFavourites(list);
      setIsFavourite(true);
      toast.success('Adicionado aos favoritos');
    }
  };

  return (
    <section
      className="pt-20 md:pt-24 px-4 md:px-12"
      data-testid="editorial-hero"
      data-series-id={series.id}
    >
      <div className="relative max-w-6xl mx-auto rounded-3xl overflow-hidden border border-ruby/40 shadow-2xl shadow-ruby/10 min-h-[420px] md:min-h-[520px]">
        {/* Background image with graceful fallback */}
        {image ? (
          <img
            src={image}
            alt={series.title}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : null}
        {/* Base dark gradient so text is always readable, even if image fails */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#12060a] to-ruby/70" />
        {/* Overlay on top of the image itself for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/70 to-black/30 md:bg-gradient-to-r md:from-black/90 md:via-black/60 md:to-transparent" />
        {/* Decorative glow */}
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-ruby/30 blur-3xl pointer-events-none" />

        <div className="relative z-10 h-full min-h-[420px] md:min-h-[520px] flex flex-col justify-end p-6 sm:p-8 md:p-12 lg:p-14 max-w-3xl">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge className="bg-ruby text-white text-xs uppercase tracking-wider px-3 py-1" data-testid="editorial-hero-label">
              {label}
            </Badge>
            {series.episode_count > 0 && (
              <Badge variant="outline" className="border-white/25 text-white/85 bg-white/5 backdrop-blur-sm text-xs">
                {series.episode_count} {series.episode_count === 1 ? 'episódio' : 'episódios'}
              </Badge>
            )}
            <Badge variant="outline" className="border-gold/50 text-gold bg-gold/10 text-xs flex items-center gap-1">
              <Crown className="w-3 h-3" /> Premium
            </Badge>
          </div>

          <h2
            className="font-brand text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 drop-shadow-lg"
            data-testid="editorial-hero-title"
          >
            {series.title}
          </h2>

          {countdown_enabled && countdown_target_date && (
            <CountdownBlock label={countdown_label || 'Próximo episódio em'} target={countdown_target_date} />
          )}

          {synopsis && (
            <p
              className="text-white/85 text-sm sm:text-base md:text-lg leading-relaxed font-reading mb-6 line-clamp-4 md:line-clamp-5"
              data-testid="editorial-hero-synopsis"
            >
              {synopsis}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleStartReading}
              data-testid="editorial-hero-start"
              className="bg-ruby hover:bg-ruby-light text-white rounded-full px-6 sm:px-8 py-5 sm:py-6 font-semibold text-sm sm:text-base shadow-lg active:scale-95"
            >
              <Play className="w-4 h-4 mr-2 fill-white" />
              Começar a ler
            </Button>
            <Button
              onClick={handleMoreInfo}
              variant="outline"
              data-testid="editorial-hero-more"
              className="border-white/40 text-white hover:bg-white/10 rounded-full px-6 sm:px-8 py-5 sm:py-6 font-semibold text-sm sm:text-base bg-black/30 backdrop-blur-sm"
            >
              <Info className="w-4 h-4 mr-2" />
              Saber mais
            </Button>
            <Button
              onClick={handleToggleFavourite}
              variant="ghost"
              data-testid="editorial-hero-favourite"
              aria-label={isFavourite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
              className="text-white hover:bg-white/10 rounded-full px-4 sm:px-5 py-5 sm:py-6 border border-white/20"
            >
              <Heart className={'w-5 h-5 ' + (isFavourite ? 'fill-ruby text-ruby' : '')} />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
